import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  AlignJustify,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Heart,
  Maximize2,
  Minimize2,
  Minus,
  MoveHorizontal,
  Plus,
  Type,
} from 'lucide-react';
import { canonicalPostUrl, isVerse, type Post } from '@shared/types';
import { AnnotatedBody } from '@/components/AnnotatedBody';
import { AuthorNote } from '@/components/AuthorNote';
import { Comments } from '@/components/Comments';
import { EndMark } from '@/components/Nib';
import { ReadingRail } from '@/components/ReadingRail';
import { ShareMenu, type ShareTarget } from '@/components/ShareMenu';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { api, ApiClientError } from '@/lib/api';
import { useSession } from '@/lib/session';
import { cn, formatDate, initials } from '@/lib/utils';
import { localProgress, reconcile, saveProgress } from '@/lib/reading';
import { formLabel, n, t } from '@/lib/i18n';

/** Reader preferences persist across posts and sessions. */
interface ReaderPrefs {
  size: number;
  leading: number;
  /** Measure — the width of the text column itself, in rem. */
  measure: number;
}

/**
 * How wide the text column may run.
 *
 * The page is full-bleed, but the *measure* is a separate question: past roughly
 * 90 characters a line the eye loses its place on the return sweep, and
 * Devanagari — with matras above and below the base line — loses it sooner than
 * Latin does. So the wide settings are offered as a reader's choice rather than
 * imposed, and the default stays where prose is comfortable.
 */
const MEASURES = [38, 46, 56] as const;

const DEFAULT_PREFS: ReaderPrefs = { size: 1.18, leading: 2, measure: MEASURES[0] };
const PREFS_KEY = 'kaha:reader';

function loadPrefs(): ReaderPrefs {
  try {
    const stored = localStorage.getItem(PREFS_KEY);
    return stored ? { ...DEFAULT_PREFS, ...(JSON.parse(stored) as Partial<ReaderPrefs>) } : DEFAULT_PREFS;
  } catch {
    return DEFAULT_PREFS;
  }
}

export function PostView() {
  const { handle, slug } = useParams<{ handle?: string; slug: string }>();
  const [params, setParams] = useSearchParams();
  const { viewer, site } = useSession();
  const tenant = site?.tenant ?? null;

  const requestedPage = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);
  /** Set when a contents entry points at another page; consumed after it loads. */
  const [pendingAnchor, setPendingAnchor] = useState<string | null>(null);
  const [immersive, setImmersive] = useState(false);
  const [progress, setProgress] = useState(0);
  /** Offered rather than forced — jumping a reader mid-page is disorienting. */
  const [resumeTo, setResumeTo] = useState<number | null>(null);

  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [prefs, setPrefs] = useState<ReaderPrefs>(loadPrefs);
  const [liking, setLiking] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setNotFound(false);

    api
      .post(slug!, handle?.replace(/^@/, ''), requestedPage, controller.signal)
      .then(({ post: found }) => {
        setPost(found);

        // Offer to resume only when the reader has not asked for a page
        // explicitly, and only when it would actually move them.
        const saved = reconcile(localProgress(found.id), found.progress);
        const untouched = !params.get('page');
        setResumeTo(saved && untouched && saved.page > 1 && saved.page <= found.pageCount ? saved.page : null);
      })
      .catch((error) => {
        if (error instanceof ApiClientError && error.status === 404) setNotFound(true);
        else if ((error as Error).name !== 'AbortError') setNotFound(true);
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [handle, slug, requestedPage, params]);

  useEffect(() => {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  }, [prefs]);

  /**
   * Records how far down the page the reader is.
   *
   * Throttled to once every 2s and written on unmount, because the interesting
   * moment is the one where they leave — closing the tab, following a link —
   * and a scroll handler that fires per frame would hammer both localStorage
   * and the API for no extra fidelity.
   */
  useEffect(() => {
    if (!post) return;
    const postId = post.id;
    const page = post.page;
    let last = 0;

    const record = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      const percent = scrollable > 0 ? window.scrollY / scrollable : 0;
      saveProgress(postId, page, percent, Boolean(viewer));
    };

    const onScroll = () => {
      const now = Date.now();
      if (now - last < 2000) return;
      last = now;
      record();
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      record();
    };
  }, [post, viewer]);

  /** Scrolls to a heading once the page carrying it has rendered. */
  useEffect(() => {
    if (!post || !pendingAnchor) return;
    const node = document.getElementById(pendingAnchor);
    node?.scrollIntoView({ block: 'start' });
    setPendingAnchor(null);
  }, [post, pendingAnchor]);

  /**
   * Immersive reading.
   *
   * Native fullscreen *and* a class on the document: fullscreen alone still
   * leaves the site's header, footer and rails on screen, which is most of what
   * a reader wants gone. The class is the part that does the work, so the mode
   * still functions where the Fullscreen API is refused (iOS Safari).
   */
  useEffect(() => {
    document.documentElement.classList.toggle('immersive', immersive);

    if (immersive && !document.fullscreenElement) {
      void document.documentElement.requestFullscreen?.().catch(() => undefined);
    } else if (!immersive && document.fullscreenElement) {
      void document.exitFullscreen?.().catch(() => undefined);
    }

    // Leaving fullscreen by Esc or the browser's own control must also leave the
    // mode, or the page stays stripped with no way back.
    const sync = () => {
      if (!document.fullscreenElement) setImmersive(false);
    };
    document.addEventListener('fullscreenchange', sync);

    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.documentElement.classList.remove('immersive');
    };
  }, [immersive]);

  /** Keyboard: pages with the arrows, F for immersive, Esc to leave it. */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      if (event.key === 'ArrowRight' && post && post.page < post.pageCount) goToPage(post.page + 1);
      else if (event.key === 'ArrowLeft' && post && post.page > 1) goToPage(post.page - 1);
      else if (event.key.toLowerCase() === 'f') setImmersive((on) => !on);
      else if (event.key === 'Escape') setImmersive(false);
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  /** How far through the page the reader is — drawn as the top rule. */
  useEffect(() => {
    const track = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(scrollable > 0 ? Math.min(1, window.scrollY / scrollable) : 0);
    };
    track();
    window.addEventListener('scroll', track, { passive: true });
    window.addEventListener('resize', track);
    return () => {
      window.removeEventListener('scroll', track);
      window.removeEventListener('resize', track);
    };
  }, [post]);

  useEffect(() => {
    if (post) document.title = `${post.title} — ${post.author.penName || post.author.displayName}`;
    return () => {
      document.title = t('कलम प्रेस — नेपाली साहित्यको घर');
    };
  }, [post]);

  const toggleLike = useCallback(async () => {
    if (!post || !viewer || liking) return;
    setLiking(true);
    try {
      const result = await api.toggleLike(post.id);
      setPost((current) =>
        current ? { ...current, likedByViewer: result.liked, likeCount: result.likeCount } : current,
      );
    } finally {
      setLiking(false);
    }
  }, [post, viewer, liking]);

  if (loading) return <PostSkeleton />;

  if (notFound || !post) {
    return (
      <div className="container max-w-2xl py-24 text-center">
        <h1 className="np text-3xl font-semibold">{t('रचना भेटिएन')}</h1>
        <p className="np mt-2 text-muted-foreground">{t('यो लिंक पुरानो वा गलत हुन सक्छ।')}</p>
        <Button className="mt-6" asChild>
          <Link to="/">
            <ArrowLeft />{t('फिडमा फर्किनुहोस्')}</Link>
        </Button>
      </div>
    );
  }

  const verse = isVerse(post.category);
  /*
   * Always the writer's own address, never window.location — a reader on the
   * platform mirror who shares the page they are looking at would otherwise send
   * everyone to a second copy of the same poem.
   */
  const shareTarget: ShareTarget = {
    url: canonicalPostUrl(post.author.siteUrl, post.slug),
    title: post.title,
    byline: post.author.penName || post.author.displayName,
  };
  const hasOutline = post.outline.some((entry) => entry.headings.length > 0);
  /* The rail only earns its column when there is something to navigate. */
  const railed = hasOutline || post.pageCount > 1;

  /**
   * Moves to a page, and optionally to a heading on it.
   *
   * A heading on the current page can be scrolled to immediately; one on another
   * page has to wait for that page's HTML to arrive, so the anchor is held until
   * the content changes.
   */
  const goToPage = (next: number, headingId?: string) => {
    if (next === post?.page && headingId) {
      document.getElementById(headingId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.history.replaceState(null, '', `#${headingId}`);
      return;
    }

    const updated = new URLSearchParams(params);
    if (next <= 1) updated.delete('page');
    else updated.set('page', String(next));
    setParams(updated);
    setPendingAnchor(headingId ?? null);
    if (!headingId) window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div
      className="reader-shell py-10"
      style={{ '--reader-measure': `${prefs.measure}rem` } as React.CSSProperties}
    >
      {/* Reading progress, drawn as a rule across the top — the newspaper's own
          motif doing a job rather than decorating. */}
      <div className="fixed inset-x-0 top-0 z-50 h-0.5 bg-transparent" aria-hidden="true">
        <div className="h-full bg-primary transition-[width] duration-150" style={{ width: `${progress * 100}%` }} />
      </div>

      <div className="reader-tools fixed bottom-6 right-6 z-40 flex flex-col gap-2">
        {/* Sharing belongs where a reader is when the piece moves them — which
            is somewhere in the middle of it, not at the footer they have not
            reached yet. */}
        <ShareMenu target={shareTarget} variant="secondary" iconOnly className="shadow-md" />
        <Button
          variant="secondary"
          size="icon"
          className="shadow-md"
          onClick={() => setImmersive((on) => !on)}
          title={immersive ? t('बाहिर निस्कनुहोस् (Esc)') : t('पूर्ण स्क्रिन (F)')}
          aria-label={immersive ? t('पूर्ण स्क्रिनबाट बाहिर') : t('पूर्ण स्क्रिन')}
        >
          {immersive ? <Minimize2 /> : <Maximize2 />}
        </Button>
      </div>

      {/*
        Three zones on a wide screen: contents in the left margin, the text
        centred, tools in the right margin. The side columns are what make the
        full-bleed page worth having — otherwise the reclaimed width is just
        empty paper either side of the column.

        Without a contents rail the grid only forms at xl, and with equal side
        columns, so the text stays optically centred instead of drifting right
        of an empty gutter.
      */}
      <div
        className={cn(
          'reader-grid lg:gap-10 xl:gap-12',
          railed
            ? 'lg:grid lg:grid-cols-[17rem_minmax(0,1fr)] xl:grid-cols-[18rem_minmax(0,1fr)_15rem]'
            : 'xl:grid xl:grid-cols-[15rem_minmax(0,1fr)_15rem]',
        )}
      >
        {/*
          Contents leads the source order as well as the layout: it is navigation
          for the piece, so a reader tabbing in should reach it before the text,
          not after all of it.
        */}
        <aside className={cn('hidden', railed ? 'lg:block' : 'xl:block')}>
          {railed && (
            <div className="sticky top-20 max-h-[calc(100vh-6rem)] overflow-y-auto pb-8 pe-2">
              <ReadingRail
                outline={post.outline}
                page={post.page}
                pageCount={post.pageCount}
                onGo={goToPage}
              />
            </div>
          )}
        </aside>

        <article className="reader-column mx-auto w-full">
      <header className="mb-8">
        <div className="mb-3 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <Badge variant="secondary" className="np">
            {formLabel(post.form)}
          </Badge>
          <span>{formatDate(post.publishedAt)}</span>
          <span className="tnum">{t('{count} मिनेट', { count: n(post.readingMinutes) })}</span>
          {post.pageCount > 1 && (
            <span className="np tnum">
              {t('पृष्ठ {page}/{total}', { page: n(post.page), total: n(post.pageCount) })}
            </span>
          )}
          {post.status === 'draft' && <Badge variant="destructive">{t('मस्यौदा')}</Badge>}
        </div>

        <h1 className="site-title np text-balance text-4xl font-semibold leading-tight sm:text-5xl">
          {post.title}
        </h1>
        {post.subtitle && <p className="np mt-3 text-xl text-muted-foreground">{post.subtitle}</p>}

        <div className="mt-6 flex items-center gap-3">
          <Link to={tenant ? '/about' : `/author/${post.author.handle}`} className="flex items-center gap-3">
            <Avatar>
              {post.author.avatarUrl ? (
                <AvatarImage src={post.author.avatarUrl} alt={post.author.displayName} />
              ) : null}
              <AvatarFallback>{initials(post.author.penName || post.author.displayName)}</AvatarFallback>
            </Avatar>
            <div>
              <div className="np font-medium leading-tight">
                {post.author.penName || post.author.displayName}
              </div>
              <div className="text-xs text-muted-foreground">@{post.author.handle}</div>
            </div>
          </Link>

          {/* Below xl there is no margin to put these in, so they ride the header. */}
          <div className="ms-auto xl:hidden">
            <ReaderControls prefs={prefs} onChange={setPrefs} />
          </div>
        </div>
      </header>

      {post.coverUrl && post.page === 1 && (
        <img src={post.coverUrl} alt="" className="mb-10 w-full rounded-xl object-cover" loading="eager" />
      )}

      {resumeTo !== null && (
        <div className="np mb-6 flex flex-wrap items-center gap-3 border-y bg-muted/40 px-4 py-3 text-sm">
          <span>
            {t('तपाईं पृष्ठ {page} मा पुग्नुभएको थियो।', { page: n(resumeTo) })}
          </span>
          <Button
            size="sm"
            onClick={() => {
              const updated = new URLSearchParams(params);
              updated.set('page', String(resumeTo));
              setParams(updated);
              setResumeTo(null);
            }}
          >{t('त्यहीँबाट जारी राख्नुहोस्')}</Button>
          <Button variant="ghost" size="sm" onClick={() => setResumeTo(null)}>{t('सुरुबाटै')}</Button>
        </div>
      )}

      <AnnotatedBody
        html={post.contentHtml}
        annotations={post.annotations}
        className={cn(verse && 'poem-body')}
        style={
          {
            '--reader-size': `${prefs.size}rem`,
            '--reader-leading': prefs.leading,
          } as React.CSSProperties
        }
      />

      {post.pageCount > 1 && (
        <Pager page={post.page} pageCount={post.pageCount} onGo={goToPage} />
      )}

      <EndMark className="mt-10" />

      {/*
        On the last page only, and after the end mark. The afterword is about the
        finished piece, so a reader meeting it on page two of a उपन्यास would be
        reading the writer's account of an ending they have not reached.
      */}
      {post.authorNoteHtml && post.page === post.pageCount && (
        <AuthorNote html={post.authorNoteHtml} />
      )}

      <Separator className="my-8" />

      <footer className="flex flex-wrap items-center gap-3">
        <Button
          variant={post.likedByViewer ? 'default' : 'outline'}
          size="sm"
          onClick={toggleLike}
          disabled={!viewer || liking}
          title={viewer ? undefined : t('मन पराउन लगइन गर्नुहोस्')}
        >
          <Heart className={cn(post.likedByViewer && 'fill-current')} />
          <span className="tnum">{n(post.likeCount)}</span>
        </Button>

        <ShareMenu target={shareTarget} />

        {post.tags.length > 0 && (
          <div className="ms-auto flex flex-wrap gap-2">
            {post.tags.map((tag) => (
              <Link key={tag} to={`/?tag=${encodeURIComponent(tag)}`}>
                <Badge variant="secondary" className="np">
                  {tag}
                </Badge>
              </Link>
            ))}
          </div>
        )}
      </footer>

      {/*
        The thread hangs off the last page, not off every one. On a उपन्यास the
        discussion belongs where the reader has finished, and repeating it under
        each page would split one conversation into seven.
      */}
      {post.page === post.pageCount && <Comments post={post} />}
        </article>

        {/*
          Type settings live out here on a wide screen. In the header they scroll
          away after the first screenful, which is precisely when a reader
          decides the type is too small.
        */}
        <aside className="hidden xl:block">
          <div className="sticky top-20 space-y-4 pb-8">
            <h2 className="kicker np">{t('पढाइ')}</h2>
            <ReaderControls prefs={prefs} onChange={setPrefs} />
            {post.pageCount > 1 && (
              <p className="np tnum text-sm text-muted-foreground">
                {t('पृष्ठ {page} / {total}', { page: n(post.page), total: n(post.pageCount) })}
              </p>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

/**
 * Page navigation for multi-page pieces.
 *
 * Numbered rather than "next/previous" alone: a reader who put a उपन्यास down
 * last week wants to see where they are in it, and the number is what they
 * remember.
 */
function Pager({
  page,
  pageCount,
  onGo,
}: {
  page: number;
  pageCount: number;
  onGo: (page: number) => void;
}) {
  return (
    <nav className="mt-10 flex items-center justify-between gap-4 border-t pt-5" aria-label={t('पृष्ठ')}>
      <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onGo(page - 1)}>
        <ChevronLeft />{t('अघिल्लो')}</Button>

      <div className="flex flex-wrap items-center justify-center gap-1.5">
        {Array.from({ length: pageCount }, (_, index) => index + 1).map((number) => (
          <button
            key={number}
            type="button"
            onClick={() => onGo(number)}
            aria-current={number === page ? 'page' : undefined}
            className={cn(
              'tnum size-8 rounded-md border text-sm transition-colors',
              number === page
                ? 'border-primary bg-primary text-primary-foreground'
                : 'hover:bg-accent',
            )}
          >
            {n(number)}
          </button>
        ))}
      </div>

      <Button variant="outline" size="sm" disabled={page >= pageCount} onClick={() => onGo(page + 1)}>{t('अर्को')}<ChevronRight />
      </Button>
    </nav>
  );
}

function ReaderControls({ prefs, onChange }: { prefs: ReaderPrefs; onChange: (prefs: ReaderPrefs) => void }) {
  const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

  return (
    <div className="flex items-center gap-1 rounded-lg border p-1">
      <Type className="mx-1 size-3.5 text-muted-foreground" />
      <Button
        variant="ghost"
        size="icon"
        className="size-7"
        aria-label={t('अक्षर सानो')}
        onClick={() => onChange({ ...prefs, size: clamp(prefs.size - 0.08, 0.95, 1.7) })}
      >
        <Minus />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="size-7"
        aria-label={t('अक्षर ठूलो')}
        onClick={() => onChange({ ...prefs, size: clamp(prefs.size + 0.08, 0.95, 1.7) })}
      >
        <Plus />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="size-7"
        aria-label={t('हरफ बीचको दूरी')}
        title={t('हरफ बीचको दूरी')}
        onClick={() => onChange({ ...prefs, leading: prefs.leading >= 2.4 ? 1.7 : prefs.leading + 0.2 })}
      >
        <AlignJustify />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="size-7"
        aria-label={t('स्तम्भको चौडाइ')}
        title={t('स्तम्भको चौडाइ')}
        onClick={() => {
          const next = MEASURES[(MEASURES.indexOf(prefs.measure as (typeof MEASURES)[number]) + 1) % MEASURES.length];
          onChange({ ...prefs, measure: next ?? MEASURES[0] });
        }}
      >
        <MoveHorizontal />
      </Button>
    </div>
  );
}

function PostSkeleton() {
  return (
    <div className="container max-w-3xl space-y-4 py-10">
      <Skeleton className="h-5 w-40" />
      <Skeleton className="h-12 w-4/5" />
      <Skeleton className="h-6 w-2/3" />
      <div className="space-y-3 pt-8">
        {[0, 1, 2, 3, 4, 5].map((row) => (
          <Skeleton key={row} className="h-5 w-full" />
        ))}
      </div>
    </div>
  );
}
